import React from 'react';

import { RequiredFlag } from '../../required-flag';
import { Option } from '../option';
import { Select } from '../select';
import { SelectOption, SingleOption, isSingleOption } from '../select-types';
import useAsyncSelectInputV2 from './async-select-input-v2.controller';

const ID = 'select-input-v2';

export interface AsyncSelectInputV2Props<T extends object, D extends string | number> {
    id?: string;
    label?: React.ReactNode | string;
    helpMessage?: React.ReactNode;

    options: SelectOption<T>[] | null;
    onSelect?: (option: SingleOption<T> | null) => void;
    value?: string | null;
    searchValueOverwrite?: string;

    singleSearch?: (value: string) => Promise<SingleOption<T>>;
    onSearch?: (value: string) => void;
    onScrollToBottom?: () => void;

    required?: boolean;
    disabled?: boolean;

    isClearable?: boolean;
    isLoading?: boolean;

    className?: string;

    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    isOpenOverwrite?: boolean;
    hideLabel?: boolean;
    customLabelAction?: React.ReactNode;
}

export const AsyncSelectInputV2 = <T extends object, D extends string | number>(props: AsyncSelectInputV2Props<T, D>) => {
    const viewProps = useAsyncSelectInputV2(props);

    return (
        <div
            id={ID}
            className={'form-group w-full ' + props.className}>
            <div className='mb-0! flex w-full flex-col'>
                {!props.hideLabel && (
                    <div className='flex justify-between'>
                        <div className='inline-flex items-center gap-1'>
                            <span className='text-foreground-default text-sm font-medium'>{props.label}</span>
                            <RequiredFlag required={props.required} />
                        </div>
                        {props.customLabelAction}
                    </div>
                )}
                {props.helpMessage && (
                    <>
                        <small className='form-text text-brand-muted'>{props.helpMessage}</small>
                    </>
                )}
            </div>

            <Select
                id={props.id || ID}
                onSelect={viewProps.onSelect}
                selected={viewProps.selected}
                value={viewProps.value}
                onChange={viewProps.onSearch}
                isClearable={props.isClearable ?? true}
                optionsLoading={viewProps.isLoading}
                onScrollToBottom={props.onScrollToBottom}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                disabled={props.disabled}
                isOpenOverwrite={props.isOpenOverwrite}>
                {viewProps.searchResultOptions &&
                    viewProps.searchResultOptions.map((option) => {
                        if (isSingleOption(option)) {
                            return (
                                <Option
                                    key={option.value}
                                    {...option}
                                    selected={viewProps.selected?.value === option.value}
                                />
                            );
                        }
                        return (
                            <React.Fragment key={option.label}>
                                {option.label}
                                {option.options.map((opt: SingleOption<T>) => (
                                    <Option
                                        key={opt.value}
                                        {...opt}
                                        selected={viewProps.selected?.value === opt.value}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    })}
            </Select>
        </div>
    );
};

export default AsyncSelectInputV2;
